/**
 * Copyright © Smartex Canada All rights reserved.
 * See LICENSE for license details.
 */

import type { SpecificEventListener } from "@/types";
import { Component } from "./component";

export class QtyInput extends Component<QtyInputEvents> {
    static selector = 'qty-input';

    static defaults: Partial<QtyInputOptions> = {
        min: 1,
        step: 1,
        allowZero: false
    };

    public element: HTMLElement;
    private inputElement: HTMLInputElement;
    private decrementButton: HTMLElement | null;
    private incrementButton: HTMLElement | null;

    public config: QtyInputOptions;
    public value: number = 0;

    private incrementListener: EventListener;
    private decrementListener: EventListener;
    private changeListener: SpecificEventListener<'change'>;

    private focusListener: SpecificEventListener<'focus'>;
    private blurListener: SpecificEventListener<'blur'>;

    private focused: boolean = false;

    constructor(element: HTMLElement, options: Partial<QtyInputOptions> = {}) {
        super();

        this.element = element;
        this.inputElement = this.element.querySelector('input')!;
        this.decrementButton = this.element.querySelector('button.decrement');
        this.incrementButton = this.element.querySelector('button.increment');

        this.config = {
            ...QtyInput.defaults,
            ...this._getConfigFromElement(),
            ...options
        } as QtyInputOptions;

        this.value = this.config.value || this.value;

        this.incrementListener = () => this.increment();
        this.decrementListener = () => this.decrement();
        this.changeListener = this._handleValueChange.bind(this);
        this.focusListener = () => setTimeout(() => this._handleFocus());
        this.blurListener = () => setTimeout(() => this._handleBlur());

        this.inputElement.addEventListener('change', this.changeListener);

        this.decrementButton?.addEventListener('click', this.decrementListener);
        this.incrementButton?.addEventListener('click', this.incrementListener);

        [this.inputElement, this.decrementButton, this.incrementButton].forEach(element => {
            element?.addEventListener('focus', this.focusListener);
            element?.addEventListener('blur', this.blurListener);
        });
    }

    /**
     * @inheritdoc
     */
    public override destroy(): void {
        super.destroy();

        this.inputElement.removeEventListener('change', this.changeListener);
        this.decrementButton?.removeEventListener('click', this.decrementListener);
        this.incrementButton?.removeEventListener('click', this.incrementListener);

        [this.inputElement, this.decrementButton, this.incrementButton].forEach(element => {
            element?.removeEventListener('focus', this.focusListener);
            element?.removeEventListener('blur', this.blurListener);
        });

        this.inputElement = null as any;
        this.decrementButton = null;
        this.incrementButton = null;
    }

    /**
     * Increment value by configured step, times the provided amount
     */
    public increment(amount: number = 1) {
        if (this.config.max < 0
            && this.config.allowZero
            && this.value + (this.config.step * amount) > this.config.max
        ) {
            this.value = 0;
        }
        else if (typeof this.inputElement.stepUp === 'function') {
            this.inputElement.stepUp(amount);
        } else {
            this.value += this.config.step * amount;
        }

        this.inputElement.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /**
     * Decrement value by configured step, times the provided amount
     */
    public decrement(amount: number = 1) {
        if (this.config.min > 0
            && this.config.allowZero
            && this.value - (this.config.step * amount) < this.config.min
        ) {
            this.value = 0;
        }
        else if (typeof this.inputElement.stepDown === 'function') {
            this.inputElement.stepDown(amount);
        }
        else {
            this.value -= this.config.step * amount;
        }

        this.inputElement.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /**
     * Retrieve config options from element
     */
    private _getConfigFromElement(): Partial<QtyInputOptions> {
        const config: Partial<QtyInputOptions> = {};

        if (this.inputElement.min)
            config.min = parseFloat(this.inputElement.min);

        if (this.inputElement.max)
            config.max = parseFloat(this.inputElement.max);

        if (this.inputElement.step)
            config.step = parseFloat(this.inputElement.step);

        if (this.inputElement.value)
            config.value = parseFloat(this.inputElement.value);

        return config;
    }

    /**
     * Handle value change and correct value if needed
     */
    private _handleValueChange() {
        this.value = this._processValue(this.inputElement.value);
        this.inputElement.value = "" + this.value;

        this.trigger('updated', this.value);
    }

    /**
     * Process value and correct if needed
     */
    private _processValue(value: string | number): number {
        if (value !== '') {
            value = parseFloat("" + value);
        }
        else if (this.config.min || this.config.min === 0) {
            value = this.config.allowZero ? 0 : this.config.min;
        }
        else {
            value = 0;
        }

        if (Number.isNaN(value)) {
            if (this.config.min && this.config.allowZero) {
                value = 0;
            } else {
                value = this.config.min || 0;
            }
        }

        if ((this.config.max || this.config.max === 0) && value > this.config.max) {
            if (this.config.max < 0 && this.config.allowZero) {
                value = 0;
            } else {
                value = this.config.max;
            }
        }

        if (this.config.min || this.config.min === 0) {
            if (this.config.allowZero && (value < this.config.min && value !== 0)) {
                value = this.config.min;
            } else if (!this.config.allowZero && value < this.config.min) {
                value = this.config.min;
            }
        }

        return value;
    }

    private _handleFocus() {
        if (!this.focused) {
            this.focused = true;
            this.trigger('focus');
        }
    }

    private _handleBlur() {
        if (!this.element.contains(document.activeElement)) {
            this.focused = false;
            this.trigger('blur');
        }
    }
}

export interface QtyInputOptions {
    min: number
    max: number
    step: number
    allowZero: boolean
    value?: number
}

export interface QtyInputEvents {
    updated: number
    focus: never
    blur: never
}
